import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import { WELLBEING_PATHS } from '../wellbeingPaths';
import { PanelSection } from './PanelSection';

export interface SessionSummaryProps {
  blocks: number;
  focusMinutes: number;
  parked: number;
  /** Recaps written this session, oldest first. */
  recaps: { id: string; intention?: string; text: string }[];
  /** Thoughts still parked when the session ended. */
  notes?: { id: string; text: string }[];
  /** Save the parked thoughts to the Worry dump; absent, no button. */
  onKeepNotes?: () => void;
  /** Back to the set-up; the session is already recorded. */
  onDismiss: () => void;
}

/**
 * The thoughts parked during the session, handed back at the end - the other
 * half of the parking-lot promise. One tap keeps them in the Worry dump, so
 * closing the summary does not lose them. The button says what it does for
 * the student, not where it files them: "buy a new notebook" is not a
 * worry. The confirmation names the place.
 */
function ParkedForLater({
  notes,
  onKeep,
}: {
  notes: { id: string; text: string }[];
  onKeep?: () => void;
}) {
  const [kept, setKept] = useState(false);
  if (kept) {
    return (
      <p
        className='flex items-center gap-2 text-sm text-foreground'
        role='status'
      >
        <CheckCircle2
          className='h-4 w-4 shrink-0 text-muted-foreground'
          aria-hidden='true'
        />
        <span>
          Saved to your{' '}
          <Link
            to={WELLBEING_PATHS.worry}
            className='tap-target font-medium underline underline-offset-4'
          >
            Worry dump
          </Link>
          .
        </span>
      </p>
    );
  }
  if (notes.length === 0) return null;
  return (
    <PanelSection title='Parked for later'>
      {/* A named, focusable region: nothing inside it is tabbable, so
        without this a keyboard user could not scroll a long list (axe's
        scrollable-region-focusable; the lint rule allows `region`). */}
      <div
        role='region'
        tabIndex={0}
        aria-label='Thoughts parked this session'
        className='max-h-40 overflow-y-auto overscroll-contain rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      >
        <ul className='space-y-1.5 text-sm text-foreground'>
          {notes.map(note => (
            <li key={note.id} className='flex gap-2'>
              <span className='text-muted-foreground' aria-hidden='true'>
                ·
              </span>
              <span className='min-w-0 break-words'>{note.text}</span>
            </li>
          ))}
        </ul>
      </div>
      {onKeep && (
        <Button
          type='button'
          variant='outline'
          size='sm'
          className='max-sm:h-11'
          onClick={() => {
            onKeep();
            setKept(true);
          }}
        >
          Save these for later
        </Button>
      )}
    </PanelSection>
  );
}

/**
 * What a finished session added up to. The reward moment, kept honest: the
 * stage beside it shows the total on a full ring, and this is the detail.
 */
export function SessionSummary(props: SessionSummaryProps) {
  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-lg font-semibold text-foreground'>Nice session.</h2>
        <p className='mt-1 text-sm text-muted-foreground'>
          {props.focusMinutes} min of focus across {props.blocks}{' '}
          {props.blocks === 1 ? 'block' : 'blocks'}
          {props.parked > 0 &&
            `, with ${props.parked} ${props.parked === 1 ? 'thought' : 'thoughts'} parked for later`}
          .
        </p>
      </div>
      {props.recaps.length > 0 && (
        <PanelSection title='What you learned'>
          <ul className='space-y-3'>
            {props.recaps.map(r => (
              <li key={r.id} className='text-sm'>
                <span className='block text-foreground'>{r.text}</span>
                {r.intention && (
                  <span className='text-xs text-muted-foreground'>
                    {r.intention}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </PanelSection>
      )}
      <ParkedForLater notes={props.notes ?? []} onKeep={props.onKeepNotes} />
      {/* The one way on: Start waits behind this, so the session gets its
        moment before the next one is offered. */}
      <Button type='button' className='max-sm:h-11' onClick={props.onDismiss}>
        Plan the next session
      </Button>
    </div>
  );
}
